import { useEffect, useLayoutEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  changePassword,
  listAuthMethods,
  listBrowserSessions,
  revokeBrowserSession,
  revokeAllBrowserSessions,
  updateMe,
  type AuthMethod,
  type BrowserSession,
  beginTotpEnrollment,
  confirmTotp,
  disableTotp,
  getTotpStatus,
  regenerateTotpRecovery,
  addPasswordMethod,
  beginOAuthMethodLink,
  beginOAuthSecurityReauth,
  beginSecurityStepUp,
  confirmEmailChange,
  removeAuthMethod,
  requestEmailChange,
} from "../../api/account";
import { apiErrorMessage } from "../../api/errorMessage";
import { validatePassword, validateProfileName } from "../../auth/validation";
import { useAuth } from "../../auth/useAuth";
import StatusMessage from "../../components/StatusMessage";
import {
  consumeOAuthReauthProof,
  createOAuthPkce,
  hasOAuthReauthProof,
  rememberOAuthReauthIntent,
  rememberStateFromRedirect,
} from "../../auth/tokens";

type MethodsState =
  | { status: "loading" }
  | { status: "loaded"; methods: AuthMethod[] }
  | { status: "error" };

type SessionsState =
  | { status: "loading" }
  | { status: "loaded"; sessions: BrowserSession[] }
  | { status: "error" };

export default function SecurityPage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [emailVerificationToken] = useState(() =>
    searchParams.get("email_change_token"),
  );
  const [hasReauthMarker] = useState(() => searchParams.has("reauth"));
  useLayoutEffect(() => {
    if (emailVerificationToken || hasReauthMarker) {
      window.history.replaceState({}, "", "/account/security");
    }
  }, [emailVerificationToken, hasReauthMarker]);
  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [lastName, setLastName] = useState(user?.last_name ?? "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [methods, setMethods] = useState<MethodsState>({ status: "loading" });
  const [sessionsState, setSessionsState] = useState<SessionsState>({
    status: "loading",
  });
  const [sessionAction, setSessionAction] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState("");

  useEffect(() => {
    let active = true;
    void listAuthMethods().then(
      (items) => {
        if (active) setMethods({ status: "loaded", methods: items });
      },
      () => {
        if (active) setMethods({ status: "error" });
      },
    );
    void listBrowserSessions().then(
      (sessions) => {
        if (active) setSessionsState({ status: "loaded", sessions });
      },
      () => {
        if (active) setSessionsState({ status: "error" });
      },
    );
    return () => {
      active = false;
    };
  }, []);

  async function retrySessions() {
    setSessionsState({ status: "loading" });
    setSessionError("");
    try {
      const sessions = await listBrowserSessions();
      setSessionsState({ status: "loaded", sessions });
    } catch {
      setSessionsState({ status: "error" });
    }
  }

  async function revokeSession(session: BrowserSession) {
    setSessionAction(session.id);
    setSessionError("");
    try {
      await revokeBrowserSession(session.id);
      setSessionsState((state) =>
        state.status === "loaded"
          ? {
              ...state,
              sessions: state.sessions.filter((item) => item.id !== session.id),
            }
          : state,
      );
    } catch (err) {
      setSessionError(
        apiErrorMessage(err, `Could not revoke ${session.label}.`),
      );
    } finally {
      setSessionAction(null);
    }
  }

  async function signOutEverywhere() {
    setSessionAction("all");
    setSessionError("");
    try {
      await revokeAllBrowserSessions();
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      setSessionError(
        apiErrorMessage(err, "Could not sign out every browser."),
      );
      setSessionAction(null);
    }
  }

  if (!user) return null;

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    setError("");
    const first = validateProfileName(firstName);
    const last = validateProfileName(lastName);
    if (!first.ok || !last.ok) {
      setError(
        !first.ok
          ? first.message
          : !last.ok
            ? last.message
            : "Check your name.",
      );
      return;
    }
    setSaving(true);
    try {
      await updateMe({
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
      });
      await refreshUser();
      setMessage("Profile updated.");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not update your profile."));
    } finally {
      setSaving(false);
    }
  }

  const passwordMethod =
    methods.status === "loaded"
      ? methods.methods.find((method) => method.source === "meridian")
      : undefined;

  return (
    <div className="account-page-content">
      <PageHeading />
      {message && <StatusMessage kind="success">{message}</StatusMessage>}
      {error && <StatusMessage kind="error">{error}</StatusMessage>}

      <section className="card">
        <div className="section-heading">
          <div>
            <h2>Profile</h2>
            <p>Used across Meridian and in account communications.</p>
          </div>
        </div>
        <form onSubmit={saveProfile} noValidate>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="first-name">First name</label>
              <input
                id="first-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                disabled={saving}
                maxLength={120}
              />
            </div>
            <div className="form-group">
              <label htmlFor="last-name">Last name</label>
              <input
                id="last-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                disabled={saving}
                maxLength={120}
              />
            </div>
          </div>
          <EmailChangeForm
            currentEmail={user.email}
            verificationToken={emailVerificationToken}
            onCompleted={() => navigate("/login?email=changed", { replace: true })}
          />
          <button
            className="btn btn-primary btn-fit"
            type="submit"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save profile"}
          </button>
        </form>
      </section>

      <section className="card" aria-labelledby="browser-sessions-heading">
        <div className="section-heading">
          <div>
            <h2 id="browser-sessions-heading">Browser sessions</h2>
            <p>
              Signed-in browsers. Network metadata is recorded only as a coarse
              prefix and is never displayed.
            </p>
          </div>
          {sessionsState.status === "loaded" &&
            sessionsState.sessions.length > 0 && (
              <button
                className="btn btn-danger btn-sm"
                type="button"
                disabled={sessionAction !== null}
                onClick={() => void signOutEverywhere()}
              >
                {sessionAction === "all"
                  ? "Signing out…"
                  : "Sign out everywhere"}
              </button>
            )}
        </div>
        {sessionError && (
          <StatusMessage kind="error">{sessionError}</StatusMessage>
        )}
        {sessionsState.status === "loading" ? (
          <p className="supporting-copy" role="status">
            Loading browser sessions…
          </p>
        ) : sessionsState.status === "error" ? (
          <div>
            <p className="error-message" role="alert">
              Browser sessions are temporarily unavailable.
            </p>
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              onClick={() => void retrySessions()}
            >
              Try again
            </button>
          </div>
        ) : sessionsState.sessions.length === 0 ? (
          <p className="supporting-copy">No active browser sessions to show.</p>
        ) : (
          <ul className="unlock-method-list" aria-label="Browser sessions">
            {sessionsState.sessions.map((session) => (
              <li key={session.id}>
                <div>
                  <strong>
                    {session.label}
                    {session.current ? " · This browser" : ""}
                  </strong>
                  <span>
                    Started {formatDate(session.created_at)} · last used{" "}
                    {formatDate(session.last_used_at)}
                  </span>
                </div>
                {!session.current && (
                  <button
                    className="btn btn-secondary btn-sm"
                    type="button"
                    disabled={sessionAction !== null}
                    onClick={() => void revokeSession(session)}
                  >
                    {sessionAction === session.id ? "Revoking…" : "Revoke"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card" aria-labelledby="sign-in-methods-heading">
        <div className="section-heading">
          <div>
            <h2 id="sign-in-methods-heading">Sign-in methods</h2>
            <p>
              Ways you can currently prove ownership of this Meridian account.
            </p>
          </div>
        </div>
        <AuthMethods
          state={methods}
          onSignedOut={() => navigate("/login?method=updated", { replace: true })}
        />
      </section>

      <section className="card">
        <div className="section-heading">
          <div>
            <h2>Password</h2>
            <p>
              Changing it verifies your current password and prevents older
              password sessions from refreshing.
            </p>
          </div>
        </div>
        {passwordMethod?.password_change_available ? (
          <PasswordForm
            onChanged={async () => {
              await logout();
              navigate("/login?password=changed", { replace: true });
            }}
          />
        ) : passwordMethod?.requires_native_password_change ? (
          <p className="supporting-copy">
            This account still has a legacy password-wrapped encryption key.
            Change the password in the Meridian app so the key can be re-wrapped
            on your trusted device without exposing it to the server.
          </p>
        ) : methods.status === "loading" ? (
          <p className="supporting-copy" role="status">
            Checking password availability…
          </p>
        ) : (
          <p className="supporting-copy">
            No password sign-in is linked to this account. Use the sign-in
            methods section above to verify with your linked provider and add one.
          </p>
        )}
      </section>

      <section className="card">
        <div className="section-heading">
          <div>
            <h2>Two-step verification</h2>
            <p>
              Require an authenticator or one-time two-step backup code after
              every primary sign-in.
            </p>
          </div>
        </div>
        <TotpManager />
      </section>
    </div>
  );
}

function TotpManager() {
  const [status, setStatus] = useState<
    "loading" | "disabled" | "enabled" | "error"
  >("loading");
  const [enrollment, setEnrollment] = useState<{
    enrollment_id: string;
    secret: string;
    otpauth_uri: string;
  } | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [oauthProofAvailable] = useState(() => hasOAuthReauthProof());
  const [recovery, setRecovery] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    void getTotpStatus().then(
      (v) => setStatus(v.enabled ? "enabled" : "disabled"),
      () => setStatus("error"),
    );
  }, []);
  async function begin() {
    setBusy(true);
    setError("");
    try {
      setEnrollment(
        await beginTotpEnrollment(
          status === "enabled" ? code : undefined,
          password,
          password ? undefined : consumeOAuthReauthProof() ?? undefined,
        ),
      );
      setCode("");
      setPassword("");
    } catch (e) {
      setError(apiErrorMessage(e, "Could not begin enrollment."));
    } finally {
      setBusy(false);
    }
  }
  async function confirm() {
    setBusy(true);
    setError("");
    try {
      if (!enrollment) return;
      const r = await confirmTotp(enrollment.enrollment_id, code);
      setRecovery(r.recovery_codes);
      setEnrollment(null);
      setStatus("enabled");
      setCode("");
    } catch (e) {
      setError(apiErrorMessage(e, "Could not verify that code."));
    } finally {
      setBusy(false);
    }
  }
  async function disable() {
    setBusy(true);
    setError("");
    try {
      await disableTotp(code);
      setStatus("disabled");
      setCode("");
      setRecovery([]);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not disable two-step verification."));
    } finally {
      setBusy(false);
    }
  }
  async function regenerate() {
    setBusy(true);
    setError("");
    try {
      const r = await regenerateTotpRecovery(code);
      setRecovery(r.recovery_codes);
      setCode("");
    } catch (e) {
      setError(
        apiErrorMessage(e, "Could not regenerate two-step backup codes."),
      );
    } finally {
      setBusy(false);
    }
  }
  if (status === "loading")
    return <p role="status">Loading two-step verification…</p>;
  if (status === "error")
    return (
      <StatusMessage kind="error">
        Two-step verification is temporarily unavailable.
      </StatusMessage>
    );
  return (
    <div>
      {error && <StatusMessage kind="error">{error}</StatusMessage>}
      {recovery.length > 0 && (
        <div>
          <h3>Save these two-step backup codes now</h3>
          <p>
            Each code signs you in once and cannot unlock your encrypted
            Meridian data. Meridian cannot show them again.
          </p>
          <pre aria-label="Two-step backup codes">{recovery.join("\n")}</pre>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() =>
              void navigator.clipboard.writeText(recovery.join("\n"))
            }
          >
            Copy backup codes
          </button>
        </div>
      )}
      {enrollment ? (
        <div>
          <p>
            Open this setup in your authenticator, or copy the secret manually.
          </p>
          <a className="btn btn-secondary btn-sm" href={enrollment.otpauth_uri}>
            Open authenticator
          </a>
          <p>
            <strong>Secret:</strong> <code>{enrollment.secret}</code>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() =>
                void navigator.clipboard.writeText(enrollment.secret)
              }
            >
              Copy secret
            </button>
          </p>
          <TotpInput code={code} setCode={setCode} disabled={busy} />
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => void confirm()}
          >
            Confirm and enable
          </button>
        </div>
      ) : status === "disabled" ? (
        <div>
          <div className="form-group">
            <label htmlFor="totp-primary-password">Current password</label>
            <input
              id="totp-primary-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || (!password && !oauthProofAvailable)}
            onClick={() => void begin()}
          >
            Set up authenticator
          </button>
        </div>
      ) : (
        <div>
          <span className="status-pill positive">Enabled</span>
          <TotpInput code={code} setCode={setCode} disabled={busy} />
          <div className="form-group">
            <label htmlFor="totp-replace-password">Current password</label>
            <input
              id="totp-replace-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
            />
          </div>
          <div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy || !code}
              onClick={() => void regenerate()}
            >
              Regenerate backup codes
            </button>{" "}
            <button
              type="button"
              className="btn btn-danger btn-sm"
              disabled={busy || !code}
              onClick={() => void disable()}
            >
              Disable
            </button>{" "}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy || !code || !password}
              onClick={() => void begin()}
            >
              Replace authenticator
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
function TotpInput({
  code,
  setCode,
  disabled,
}: {
  code: string;
  setCode: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="form-group">
      <label htmlFor="totp-code">Current authenticator code</label>
      <input
        id="totp-code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        inputMode="numeric"
        autoComplete="one-time-code"
        disabled={disabled}
      />
    </div>
  );
}

function AuthMethods({
  state,
  onSignedOut,
}: {
  state: MethodsState;
  onSignedOut: () => void;
}) {
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  async function proof() {
    if (password) return { password };
    if (!totpCode) {
      const oauth_reauth_token = consumeOAuthReauthProof();
      if (oauth_reauth_token) return { oauth_reauth_token };
      throw new Error("Enter your current password, an authenticator code, or verify with a linked provider.");
    }
    const step = await beginSecurityStepUp();
    return { challenge: step.challenge, totp_code: totpCode };
  }

  async function reauthenticate(provider: "google" | "microsoft") {
    setBusy(`reauth-${provider}`);
    setError("");
    try {
      const { challenge } = await createOAuthPkce("reauth");
      const result = await beginOAuthSecurityReauth(provider, challenge);
      rememberOAuthReauthIntent("security");
      rememberStateFromRedirect(result.url);
      window.location.assign(result.url);
    } catch (err) {
      setError(apiErrorMessage(err, `Could not verify with ${provider}.`));
      setBusy("");
    }
  }

  async function addOAuth(provider: "google" | "microsoft") {
    setBusy(provider);
    setError("");
    try {
      const { challenge } = await createOAuthPkce();
      const result = await beginOAuthMethodLink(provider, await proof(), challenge);
      rememberStateFromRedirect(result.url);
      window.location.assign(result.url);
    } catch (err) {
      setError(apiErrorMessage(err, `Could not link ${provider}.`));
      setBusy("");
    }
  }

  async function remove(source: string) {
    if (!window.confirm("Remove this sign-in method from your account?")) return;
    setBusy(source);
    setError("");
    try {
      await removeAuthMethod(source, await proof());
      onSignedOut();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not remove that sign-in method."));
      setBusy("");
    }
  }

  async function addPassword() {
    setBusy("meridian");
    setError("");
    try {
      const valid = validatePassword(newPassword, "register");
      if (!valid.ok) throw new Error(valid.message);
      await addPasswordMethod(newPassword, await proof());
      onSignedOut();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not add password sign-in."));
      setBusy("");
    }
  }
  if (state.status === "loading") {
    return (
      <p className="supporting-copy" role="status">
        Loading sign-in methods…
      </p>
    );
  }
  if (state.status === "error") {
    return (
      <p className="error-message" role="alert">
        Sign-in method status is temporarily unavailable.
      </p>
    );
  }
  return (
    <div>
      {error && <StatusMessage kind="error">{error}</StatusMessage>}
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="method-current-password">Security proof password</label>
          <input id="method-current-password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={!!busy} />
        </div>
        <div className="form-group">
          <label htmlFor="method-totp">Or authenticator / backup code</label>
          <input id="method-totp" autoComplete="one-time-code" value={totpCode} onChange={(event) => setTotpCode(event.target.value)} disabled={!!busy || !!password} />
        </div>
      </div>
      {state.methods
        .filter((method) => method.linked && (method.source === "google" || method.source === "microsoft"))
        .slice(0, 1)
        .map((method) => (
          <button
            key={`reauth-${method.source}`}
            className="btn btn-secondary btn-sm"
            type="button"
            disabled={!!busy}
            onClick={() => void reauthenticate(method.source as "google" | "microsoft")}
          >
            {busy === `reauth-${method.source}` ? "Redirecting…" : `Verify with ${method.label}`}
          </button>
        ))}
      <ul className="unlock-method-list" aria-label="Sign-in methods">
      {state.methods.map((method) => (
        <li key={method.source}>
          <div>
            <strong>{method.label}</strong>
            <span>
              {method.linked_at
                ? `Linked ${formatDate(method.linked_at)}`
                : method.configured
                  ? "Not linked"
                  : "Not configured on this Meridian deployment"}
            </span>
          </div>
          <div>
            <span className={`status-pill ${method.linked ? "positive" : "neutral"}`}>
              {method.linked ? "Linked" : "Not linked"}
            </span>{" "}
            {method.linked && method.removal_available && (
              <button aria-label={`Remove ${method.label}`} className="btn btn-danger btn-sm" type="button" disabled={!!busy} onClick={() => void remove(method.source)}>
                {busy === method.source ? "Removing…" : "Remove"}
              </button>
            )}
            {!method.linked && method.configured && method.source !== "meridian" && (
              <button aria-label={`Link ${method.label}`} className="btn btn-secondary btn-sm" type="button" disabled={!!busy} onClick={() => void addOAuth(method.source as "google" | "microsoft")}>
                {busy === method.source ? "Redirecting…" : "Link"}
              </button>
            )}
          </div>
        </li>
      ))}
      </ul>
      {state.methods.some((method) => method.source === "meridian" && !method.linked && method.configured) && (
        <div className="form-group">
          <label htmlFor="add-password">New password</label>
          <input id="add-password" type="password" autoComplete="new-password" minLength={12} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} disabled={!!busy} />
          <button className="btn btn-secondary btn-sm" type="button" disabled={!!busy || !newPassword} onClick={() => void addPassword()}>
            {busy === "meridian" ? "Adding…" : "Add password sign-in"}
          </button>
        </div>
      )}
      <p className="supporting-copy">For safety, changing sign-in methods signs every existing session out.</p>
    </div>
  );
}

function EmailChangeForm({
  currentEmail,
  verificationToken,
  onCompleted,
}: {
  currentEmail: string;
  verificationToken: string | null;
  onCompleted: () => void;
}) {
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  async function proof() {
    if (password) return { password };
    if (!totpCode) {
      const oauth_reauth_token = consumeOAuthReauthProof();
      if (oauth_reauth_token) return { oauth_reauth_token };
      throw new Error("Enter your current password, an authenticator code, or verify with a linked provider.");
    }
    const step = await beginSecurityStepUp();
    return { challenge: step.challenge, totp_code: totpCode };
  }
  async function submit() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const securityProof = await proof();
      if (verificationToken) {
        await confirmEmailChange(verificationToken, securityProof);
        onCompleted();
      } else {
        await requestEmailChange(newEmail, securityProof);
        setMessage("Check the new address for a verification link. The link expires in one hour.");
      }
    } catch (err) {
      setError(apiErrorMessage(err, "Could not change your email address."));
      setBusy(false);
    }
  }
  return (
    <fieldset className="form-group">
      <legend>Email</legend>
      <p><strong>{currentEmail}</strong></p>
      <p className="supporting-copy">Meridian will verify the new address and step up your current sign-in before changing it.</p>
      {message && <StatusMessage kind="success">{message}</StatusMessage>}
      {error && <StatusMessage kind="error">{error}</StatusMessage>}
      {!verificationToken && (
        <input type="email" autoComplete="email" placeholder="New email address" value={newEmail} onChange={(event) => setNewEmail(event.target.value)} disabled={busy} />
      )}
      <input type="password" autoComplete="current-password" placeholder="Current password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} />
      <input autoComplete="one-time-code" placeholder="Or authenticator / backup code" value={totpCode} onChange={(event) => setTotpCode(event.target.value)} disabled={busy || !!password} />
      <button className="btn btn-secondary btn-sm" type="button" disabled={busy || (!verificationToken && !newEmail)} onClick={() => void submit()}>
        {busy ? "Working…" : verificationToken ? "Verify and change email" : "Send verification email"}
      </button>
    </fieldset>
  );
}

function PasswordForm({ onChanged }: { onChanged: () => Promise<void> }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const current = validatePassword(currentPassword, "login");
    const next = validatePassword(newPassword, "register");
    if (!current.ok || !next.ok) {
      setError(
        !current.ok
          ? current.message
          : !next.ok
            ? next.message
            : "Check your password.",
      );
      return;
    }
    if (newPassword !== confirmation) {
      setError("Those new passwords don't match.");
      return;
    }
    if (currentPassword === newPassword) {
      setError(
        "Choose a new password that is different from your current password.",
      );
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      await onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not change your password."));
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="form-group">
        <label htmlFor="current-password">Current password</label>
        <input
          id="current-password"
          type="password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          autoComplete="current-password"
          disabled={submitting}
        />
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="new-password">New password</label>
          <input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            autoComplete="new-password"
            minLength={12}
            disabled={submitting}
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirm-password">Confirm new password</label>
          <input
            id="confirm-password"
            type="password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="new-password"
            minLength={12}
            disabled={submitting}
          />
        </div>
      </div>
      {error && <StatusMessage kind="error">{error}</StatusMessage>}
      <button
        className="btn btn-primary btn-fit"
        type="submit"
        disabled={submitting}
      >
        {submitting ? "Changing…" : "Change password"}
      </button>
    </form>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "on an unknown date"
    : `on ${date.toLocaleDateString()}`;
}

function PageHeading() {
  return (
    <div className="page-heading">
      <p className="eyebrow">Identity</p>
      <h1>Security</h1>
      <p>Keep your personal details and sign-in credentials current.</p>
    </div>
  );
}
